/**
 * ============================================
 * ALERT CONTEXT
 * ============================================
 * 
 * Global state for alerts, notifications, and alarm management.
 * Includes startup grace period to prevent false alarms on page load/reconnect.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import type { Alert, AlertHistoryItem } from '@/types';
import { BACKEND_URL } from '@/config';
import {
  requestNotificationPermission,
  getNotificationPermission,
  triggerAlert,
  stopAlarm,
  isAlarmPlaying,
} from '@/utils/notifications';

// Grace period in milliseconds (30 seconds for testing, increase for production)
const STARTUP_GRACE_PERIOD_MS = 30000;

// ============================================
// STATE
// ============================================

interface AlertState {
  activeAlerts: Alert[];
  alertHistory: AlertHistoryItem[];
  isAlarmActive: boolean;
  notificationPermission: NotificationPermission;
  inGracePeriod: boolean;
}

type AlertAction =
  | { type: 'ADD_ALERT'; payload: Alert }
  | { type: 'ACKNOWLEDGE_ALERT'; payload: string }
  | { type: 'CLEAR_ALERT'; payload: string }
  | { type: 'RESOLVE_ALERT'; payload: string }
  | { type: 'STOP_ALARM' }
  | { type: 'SYNC_ALARM' }
  | { type: 'SET_PERMISSION'; payload: NotificationPermission }
  | { type: 'LOAD_HISTORY'; payload: AlertHistoryItem[] }
  | { type: 'SET_GRACE_PERIOD'; payload: boolean };

const initialState: AlertState = {
  activeAlerts: [],
  alertHistory: [],
  isAlarmActive: false,
  notificationPermission: 'default',
  inGracePeriod: true, // Start in grace period
};

function alertReducer(state: AlertState, action: AlertAction): AlertState {
  switch (action.type) {
    case 'ADD_ALERT': {
      // Find existing alert with the same (type, env) -- frontend dedupe key.
      const existing = state.activeAlerts.find(
        a => a.type === action.payload.type && a.environmentId === action.payload.environmentId
      );

      // Preserve a locally-acknowledged flag across re-deliveries of the same
      // logical alert. Without this, when polling re-fetches an alert the
      // user just acked (and the backend ack POST hasn't landed yet, OR the
      // engine recreated the alert with a fresh acknowledged=false), this
      // reducer would clobber the local ack -> alarm rings again. Three
      // upstream layers (backend preserve-ack, frontend POST sync,
      // triggerAlert ack-gate) all rely on this NOT being clobbered.
      const incoming: Alert = (existing && existing.acknowledged)
        ? { ...action.payload, acknowledged: true }
        : action.payload;

      const isTestAlert = incoming.id.startsWith('test-') || incoming.id.startsWith('simulated-');
      // Don't (re)activate the alarm for an already-ack'd alert.
      const shouldActivateAlarm = incoming.severity === 'critical' &&
        !incoming.acknowledged &&
        (isTestAlert || !state.inGracePeriod);

      if (existing) {
        return {
          ...state,
          activeAlerts: state.activeAlerts.map(a =>
            a.type === incoming.type && a.environmentId === incoming.environmentId
              ? incoming
              : a
          ),
          isAlarmActive: shouldActivateAlarm || state.isAlarmActive,
        };
      }

      return {
        ...state,
        activeAlerts: [...state.activeAlerts, incoming],
        isAlarmActive: shouldActivateAlarm || state.isAlarmActive,
      };
    }

    case 'ACKNOWLEDGE_ALERT': {
      const updated = state.activeAlerts.map(a =>
        a.id === action.payload ? { ...a, acknowledged: true } : a
      );
      // If the alert just acked was the LAST un-acked critical, the alarm
      // should stop ringing. Recompute isAlarmActive based on remaining
      // un-acked criticals. Without this, clicking the per-alert ack
      // button in the bell silenced the alert visually but the audible
      // alarm kept ringing (only dismissAlarm explicitly stopped it).
      const stillRinging = updated.some(
        a => a.severity === 'critical' && !a.acknowledged
      );
      return {
        ...state,
        activeAlerts: updated,
        isAlarmActive: stillRinging && state.isAlarmActive,
      };
    }
    
    case 'CLEAR_ALERT': {
      const remaining = state.activeAlerts.filter(a => a.id !== action.payload);
      const hasCritical = remaining.some(a => a.severity === 'critical' && !a.acknowledged);
      return {
        ...state,
        activeAlerts: remaining,
        isAlarmActive: !state.inGracePeriod && hasCritical,
      };
    }
    
    case 'RESOLVE_ALERT': {
      const alert = state.activeAlerts.find(a => a.id === action.payload);
      if (!alert) return state;

      const historyItem: AlertHistoryItem = {
        ...alert,
        resolvedAt: new Date(),
        durationMs: Date.now() - alert.timestamp.getTime(),
      };

      const remaining = state.activeAlerts.filter(a => a.id !== action.payload);
      const hasCritical = remaining.some(a => a.severity === 'critical' && !a.acknowledged);

      return {
        ...state,
        activeAlerts: remaining,
        alertHistory: [historyItem, ...state.alertHistory].slice(0, 100),
        isAlarmActive: !state.inGracePeriod && hasCritical,
      };
    }
    
    case 'STOP_ALARM': {
      return { ...state, isAlarmActive: false };
    }
    
    case 'SYNC_ALARM': {
      // Re-evaluate if alarm should be active based on current alerts
      // But only if not in grace period
      if (state.inGracePeriod) {
        return { ...state, isAlarmActive: false };
      }
      const hasCritical = state.activeAlerts.some(
        a => a.severity === 'critical' && !a.acknowledged
      );
      return { ...state, isAlarmActive: hasCritical };
    }
    
    case 'SET_PERMISSION': {
      return { ...state, notificationPermission: action.payload };
    }
    
    case 'LOAD_HISTORY': {
      return { ...state, alertHistory: action.payload };
    }
    
    case 'SET_GRACE_PERIOD': {
      return { ...state, inGracePeriod: action.payload };
    }
    
    default:
      return state;
  }
}

// ============================================
// CONTEXT
// ============================================

interface AlertContextValue {
  state: AlertState;
  addAlert: (alert: Alert, bypassGracePeriod?: boolean) => void;
  acknowledgeAlert: (alertId: string) => void;
  clearAlert: (alertId: string) => void;
  resolveAlert: (alertId: string) => void;
  dismissAlarm: () => void;
  syncAlarm: () => void;
  requestPermission: () => Promise<void>;
}

const AlertContext = createContext<AlertContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(alertReducer, {
    ...initialState,
    notificationPermission: getNotificationPermission(),
  });

  // Track known alert IDs to detect truly new alerts after grace period
  const knownAlertIdsRef = useRef<Set<string>>(new Set());
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inGracePeriodRef = useRef<boolean>(true);

  // Startup grace period - prevent alarms for first 2 minutes
  useEffect(() => {
    console.log('🛡️ Startup grace period active (2 minutes)');
    inGracePeriodRef.current = true;
    
    graceTimerRef.current = setTimeout(() => {
      console.log('✅ Grace period ended - alarms enabled');
      inGracePeriodRef.current = false;
      dispatch({ type: 'SET_GRACE_PERIOD', payload: false });
    }, STARTUP_GRACE_PERIOD_MS);

    return () => {
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
      }
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission().then(permission => {
      dispatch({ type: 'SET_PERMISSION', payload: permission });
    });
  }, []);

  // Load history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('lokko_alert_history');
      if (saved) {
        const history = JSON.parse(saved).map((item: AlertHistoryItem) => ({
          ...item,
          timestamp: new Date(item.timestamp),
          resolvedAt: item.resolvedAt ? new Date(item.resolvedAt) : undefined,
        }));
        dispatch({ type: 'LOAD_HISTORY', payload: history });
      }
    } catch (e) {
      console.error('Failed to load alert history:', e);
    }
  }, []);

  // Save history to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('lokko_alert_history', JSON.stringify(state.alertHistory));
    } catch (e) {
      console.error('Failed to save alert history:', e);
    }
  }, [state.alertHistory]);

  // Sync alarm state
  useEffect(() => {
    if (!state.isAlarmActive && isAlarmPlaying()) {
      stopAlarm();
    }
  }, [state.isAlarmActive]);

  // Prune knownAlertIdsRef when alerts disappear from active list. Without
  // this, an alert that fires once, gets cleared by the backend, then
  // re-fires (e.g. failover engine recreating the lifecycle alerts on each
  // switchover) would be considered "known" -> no notification fires the
  // 2nd time. Cleanup runs whenever activeAlerts changes; if a tracked
  // key is no longer in the active list, drop it so the next appearance
  // is treated as new and triggers a notification.
  useEffect(() => {
    const liveKeys = new Set(state.activeAlerts.map(a => `${a.type}-${a.environmentId}`));
    for (const key of Array.from(knownAlertIdsRef.current)) {
      if (!liveKeys.has(key)) {
        knownAlertIdsRef.current.delete(key);
      }
    }
  }, [state.activeAlerts]);

  const addAlert = useCallback((alert: Alert, bypassGracePeriod = false) => {
    const alertKey = `${alert.type}-${alert.environmentId}`;
    
    // Test/simulated alerts bypass grace period
    const isTestAlert = alert.id.startsWith('test-') || alert.id.startsWith('simulated-');
    const shouldBypass = bypassGracePeriod || isTestAlert;
    
    // During grace period: track alerts but don't trigger alarm (unless bypassed)
    if (inGracePeriodRef.current && !shouldBypass) {
      console.log(`🛡️ [Grace] Tracking alert: ${alert.title} (no alarm)`);
      knownAlertIdsRef.current.add(alertKey);
      dispatch({ type: 'ADD_ALERT', payload: alert });
      return;
    }
    
    // After grace period (or bypassed): fire notification + (alarm if critical)
    // for NEW alerts. The previous gate `severity === 'critical'` here meant
    // info/warning alerts were silent on Web AND mobile (mobile polls the
    // same /api/v1/alerts and follows the same flow). Failover lifecycle
    // events (severity=info) were getting added to the context but never
    // notifying. triggerAlert internally gates playAlarm to critical, so
    // moving the severity check there is the correct behaviour: notify on
    // every new alert; alarm only on critical.
    const isNewAlert = !knownAlertIdsRef.current.has(alertKey);
    knownAlertIdsRef.current.add(alertKey);

    if (isNewAlert || shouldBypass) {
      const sevLabel = alert.severity === 'critical' ? '🚨 critical' : `🔔 ${alert.severity}`;
      console.log(`${shouldBypass ? 'TEST/SIMULATED' : 'NEW'} ${sevLabel} alert: ${alert.title}`);
      triggerAlert(alert);
    } else {
      console.log(`📋 Known alert updated: ${alert.title}`);
    }

    dispatch({ type: 'ADD_ALERT', payload: alert });
  }, []); // No dependencies needed - using refs

  const acknowledgeAlert = useCallback((alertId: string) => {
    dispatch({ type: 'ACKNOWLEDGE_ALERT', payload: alertId });
    // Propagate ack to backend so subsequent recreations of the same
    // alert key (engine fires every tick on a persistent condition) keep
    // the acknowledged flag and don't re-ring the alarm. Without this
    // POST, the local ack is wiped on the next poll because backend
    // still sees the alert as un-acknowledged.
    fetch(`${BACKEND_URL}/api/v1/alerts/acknowledge/${encodeURIComponent(alertId)}`, {
      method: 'POST',
    }).catch(err => console.warn('Failed to sync ack to backend:', err));
  }, []);

  const clearAlert = useCallback((alertId: string) => {
    dispatch({ type: 'CLEAR_ALERT', payload: alertId });
  }, []);

  const resolveAlert = useCallback((alertId: string) => {
    // Remove from known alerts so if it comes back it will alarm
    const alert = state.activeAlerts.find(a => a.id === alertId);
    if (alert) {
      const alertKey = `${alert.type}-${alert.environmentId}`;
      knownAlertIdsRef.current.delete(alertKey);
    }
    dispatch({ type: 'RESOLVE_ALERT', payload: alertId });
  }, [state.activeAlerts]);

  const dismissAlarm = useCallback(() => {
    stopAlarm();
    dispatch({ type: 'STOP_ALARM' });
    // Acknowledge all critical alerts -- locally AND on the backend.
    // Backend ack is what keeps the alarm silent across recreations of
    // the same alert key (engine fires every tick on persistent conditions
    // like failover_exhausted; without backend sync, each new alert id
    // resets ack and the alarm rings again).
    state.activeAlerts
      .filter(a => a.severity === 'critical' && !a.acknowledged)
      .forEach(a => dispatch({ type: 'ACKNOWLEDGE_ALERT', payload: a.id }));
    // One bulk-ack call instead of N per-alert calls.
    fetch(`${BACKEND_URL}/api/v1/alerts/acknowledge`, {
      method: 'POST',
    }).catch(err => console.warn('Failed to sync bulk-ack to backend:', err));
  }, [state.activeAlerts]);

  const syncAlarm = useCallback(() => {
    dispatch({ type: 'SYNC_ALARM' });
  }, []);

  const requestPermission = useCallback(async () => {
    const permission = await requestNotificationPermission();
    dispatch({ type: 'SET_PERMISSION', payload: permission });
  }, []);

  return (
    <AlertContext.Provider value={{
      state,
      addAlert,
      acknowledgeAlert,
      clearAlert,
      resolveAlert,
      dismissAlarm,
      syncAlarm,
      requestPermission,
    }}>
      {children}
    </AlertContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useAlerts(): AlertContextValue {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within AlertProvider');
  }
  return context;
}